import { useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Bug, DatabaseZap } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/shared/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/ui/card";

const DebugErrors = () => {
  const [isSupabaseTestRunning, setIsSupabaseTestRunning] = useState(false);
  const [lastSupabaseResult, setLastSupabaseResult] = useState<string | null>(null);
  const [shouldCrashBoundary, setShouldCrashBoundary] = useState(false);

  if (shouldCrashBoundary) {
    throw new Error("Sentry boundary test crash from /debug-errors");
  }

  const triggerUnhandledException = () => {
    setTimeout(() => {
      throw new Error("Sentry unhandled exception test from /debug-errors");
    }, 0);
  };

  const triggerMalformedSupabaseRequest = async () => {
    setIsSupabaseTestRunning(true);
    setLastSupabaseResult(null);

    try {
      const { data, error } = await supabase.functions.invoke("__debug_missing_function__", {
        body: {
          source: "/debug-errors",
          at: new Date().toISOString(),
        },
      });

      if (error) {
        setLastSupabaseResult(`Received error: ${error.message}`);
        return;
      }

      const serialized = JSON.stringify(data);
      setLastSupabaseResult(`Unexpected success: ${serialized ?? "empty"}`);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      setLastSupabaseResult(`Request threw: ${message}`);
    } finally {
      setIsSupabaseTestRunning(false);
    }
  };

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-6">
        <div className="rounded-xl border bg-card p-6 shadow-card">
          <h1 className="text-2xl font-semibold">Debug Sentry Errors</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Use these actions to verify Sentry capture paths: unhandled exceptions, Supabase HTTP failures, and
            React error boundary fallback.
          </p>
          <div className="mt-4">
            <Button asChild variant="outline" size="sm">
              <Link to="/">Back to home</Link>
            </Button>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <Card className="shadow-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Bug className="h-4 w-4" />
                Unhandled exception
              </CardTitle>
              <CardDescription>
                Throws an async error outside React render flow to test global unhandled exception capture.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button type="button" onClick={triggerUnhandledException}>
                Trigger unhandled exception
              </Button>
            </CardContent>
          </Card>

          <Card className="shadow-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <DatabaseZap className="h-4 w-4" />
                Malformed Supabase request
              </CardTitle>
              <CardDescription>
                Calls a non-existent Supabase function to produce a non-2xx response and test fetch
                instrumentation.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button type="button" onClick={() => void triggerMalformedSupabaseRequest()} disabled={isSupabaseTestRunning}>
                {isSupabaseTestRunning
                  ? "Running request..."
                  : "Trigger malformed Supabase request"}
              </Button>
              {lastSupabaseResult ? (
                <p className="rounded-md border bg-muted/40 p-2 text-xs text-muted-foreground">{lastSupabaseResult}</p>
              ) : null}
            </CardContent>
          </Card>

          <Card className="shadow-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <AlertTriangle className="h-4 w-4" />
                React boundary crash
              </CardTitle>
              <CardDescription>
                Forces a render-time crash in this page so the app-level Sentry ErrorBoundary fallback is shown.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                type="button"
                variant="destructive"
                onClick={() => {
                  setShouldCrashBoundary(true);
                }}
              >
                Crash this page
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default DebugErrors;
